import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components';
import { DoctorSnippetFormDialog } from '@/components/doctor/DoctorSnippetFormDialog.tsx';
import {
  ChevronDownIcon,
  DragGripIcon,
  GlobeIcon,
  HospitalCrossIcon,
  MessageIcon,
  PencilIcon,
  PlusIcon,
  SparkleIcon,
  TrashIcon,
  UserIcon,
} from '@/components/icons.tsx';
import { useIsOperator } from '@/auth.ts';
import { useAuthFetch } from '@/hooks';
import { stripRichTextSyntax } from '@/utils';
import type { DoctorMessageSnippet, DoctorMessageSnippetsResponse } from '@dermalink/shared';
import styles from './SnippetsPanel.module.css';

type SnippetScope = 'global' | 'org' | 'user';
type ModalState =
  | null
  | { mode: 'create'; scope: SnippetScope }
  | { mode: 'edit'; snippet: DoctorMessageSnippet };

interface SnippetsPanelProps {
  onAppend: (body: string) => void;
  disabled?: boolean;
}

function previewBody(body: string, max = 72): string {
  const line = body.split(/\r?\n/).find((l) => l.trim()) ?? body;
  const t = stripRichTextSyntax(line.trim());
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

export function SnippetsPanel({ onAppend, disabled }: SnippetsPanelProps) {
  const authFetch   = useAuthFetch();
  const isOperator  = useIsOperator();
  const queryClient = useQueryClient();

  const [filter, setFilter]       = useState('');
  const [collapsed, setCollapsed] = useState<Record<SnippetScope, boolean>>({ global: false, org: false, user: false });
  const [modal, setModal]         = useState<ModalState>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DoctorMessageSnippet | null>(null);
  const [dragId, setDragId]   = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<SnippetScope | null>(null);

  const { data } = useQuery({
    queryKey: ['doctor-message-snippets'],
    queryFn: async () => {
      const res = await authFetch('/doctor/message-snippets');
      if (!res.ok) throw new Error();
      return (await res.json()) as DoctorMessageSnippetsResponse;
    },
  });

  const snippets = data?.snippets ?? [];
  const orgName  = data?.org_name ?? null;
  const hasOrg   = data?.org_id != null;

  const postMutation = useMutation({
    mutationFn: async ({ title, body, scope, is_automatic }: { title: string; body: string; scope: SnippetScope; is_automatic: boolean }) => {
      const res = await authFetch('/doctor/message-snippets', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ title, body, scope, is_automatic }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? 'Opslaan mislukt');
      }
    },
    onSuccess: () => { setModal(null); setFormError(null); queryClient.invalidateQueries({ queryKey: ['doctor-message-snippets'] }); },
    onError: (e: Error) => setFormError(e.message),
  });

  const patchMutation = useMutation({
    mutationFn: async (args: { id: number; title?: string; body?: string; scope?: SnippetScope; is_automatic?: boolean }) => {
      const res = await authFetch(`/doctor/message-snippets/${args.id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ title: args.title, body: args.body, scope: args.scope, is_automatic: args.is_automatic }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? 'Opslaan mislukt');
      }
    },
    onSuccess: () => { setModal(null); setFormError(null); queryClient.invalidateQueries({ queryKey: ['doctor-message-snippets'] }); },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await authFetch(`/doctor/message-snippets/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? 'Verwijderen mislukt');
      }
    },
    onSuccess: () => { setDeleteTarget(null); queryClient.invalidateQueries({ queryKey: ['doctor-message-snippets'] }); },
  });

  const saving = postMutation.isPending || patchMutation.isPending;

  const q = filter.toLowerCase().trim();
  const matches = (s: DoctorMessageSnippet) =>
    !q || s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q);

  const byScope: Record<SnippetScope, DoctorMessageSnippet[]> = {
    global: snippets.filter((s) => s.scope === 'global'),
    org:    snippets.filter((s) => s.scope === 'org'),
    user:   snippets.filter((s) => s.scope === 'user'),
  };

  const draggedSnippet = dragId !== null ? (snippets.find((s) => s.id === dragId) ?? null) : null;

  function isDragValid(targetScope: SnippetScope): boolean {
    if (!draggedSnippet) return false;
    if (draggedSnippet.scope === targetScope) return false;
    if (!isOperator && (draggedSnippet.scope === 'global' || targetScope === 'global')) return false;
    return true;
  }

  function handleDrop(targetScope: SnippetScope) {
    setDragOver(null);
    if (!isDragValid(targetScope)) return;
    patchMutation.mutate({ id: dragId!, scope: targetScope });
  }

  function renderGroup(scope: SnippetScope, label: string, icon: React.ReactNode, iconClass: string, canManage: boolean) {
    const all     = byScope[scope];
    const visible = all.filter(matches);
    // Collapse when: user collapsed it AND no active search (auto-expand on filter match)
    const isCollapsed = q ? visible.length === 0 : collapsed[scope];

    const dragBodyClass = dragOver === scope
      ? (isDragValid(scope) ? styles.dragOver : styles.dragOverInvalid)
      : '';

    return (
      <div key={scope} className={`${styles.scopeGroup} ${isCollapsed ? styles.collapsed : ''}`}>
        <div
          className={styles.scopeHeader}
          role="button"
          tabIndex={0}
          onClick={() => setCollapsed((c) => ({ ...c, [scope]: !c[scope] }))}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCollapsed((c) => ({ ...c, [scope]: !c[scope] })); } }}
        >
          <ChevronDownIcon size={14} className={styles.chevron} />
          <span className={`${styles.scopeIcon} ${iconClass}`}>{icon}</span>
          <span className={styles.scopeName}>{label}</span>
          <div className={styles.scopeTrailing}>
            {canManage && (
              <button
                type="button"
                className={styles.scopeAddBtn}
                onClick={(e) => { e.stopPropagation(); setFormError(null); setModal({ mode: 'create', scope }); }}
                aria-label={`Snippet toevoegen in ${label}`}
              ><PlusIcon size={8} /></button>
            )}
            <span className={styles.scopeCount}>{q ? visible.length : all.length}</span>
          </div>
        </div>

        {!isCollapsed && (
          <div
            className={`${styles.scopeBody} ${dragBodyClass}`}
            onDragOver={(e) => { if (isDragValid(scope)) e.preventDefault(); setDragOver(scope); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(null); }}
            onDrop={() => handleDrop(scope)}
          >
            {q && visible.length === 0 && <span className={styles.noResults}>Geen resultaten</span>}
            {!q && canManage && all.length === 0 && (
              <div className={styles.emptyUser}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className={styles.emptyUserIcon} aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M7 10h6M10 7v6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                <p className={styles.emptyUserText}>
                  {scope === 'user' ? 'Nog geen eigen teksten.' : scope === 'org' ? 'Nog geen organisatie-teksten.' : 'Nog geen platformteksten.'}<br />
                  Gebruik de + in de sectie-header.
                </p>
              </div>
            )}
            {visible.map((s) => (
              <div
                key={s.id}
                className={`${styles.snippetRow} ${dragId === s.id ? styles.dragging : ''}`}
                draggable={canManage}
                onDragStart={() => setDragId(s.id)}
                onDragEnd={() => { setDragId(null); setDragOver(null); }}
                onMouseUp={() => { if (!disabled) onAppend(s.body); }}
              >
                <span className={styles.dragHandle}>{canManage && <DragGripIcon />}</span>
                <button
                  type="button"
                  className={styles.appendBtn}
                  title="Toevoegen aan bericht"
                  disabled={disabled}
                  aria-label={`Voeg toe: ${s.title}`}
                ><MessageIcon size={13} /></button>
                <div className={styles.snippetMain}>
                  <div className={styles.snippetTitle}>
                    <span className={styles.snippetTitleText}>{s.title}</span>
                    {s.is_automatic && <span className={styles.automaticBadge} title="Automatische snippet"><SparkleIcon size={10} /></span>}
                  </div>
                  <div className={styles.snippetPreview}>{previewBody(s.body)}</div>
                </div>
                {canManage && (
                  <div className={styles.rowActions}>
                    <button type="button" className={styles.iconBtn} title="Bewerken" onClick={() => { setFormError(null); setModal({ mode: 'edit', snippet: s }); }} onMouseUp={(e) => e.stopPropagation()} aria-label={`Bewerken: ${s.title}`}><PencilIcon size={13} /></button>
                    <button type="button" className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Verwijderen" onClick={() => setDeleteTarget(s)} onMouseUp={(e) => e.stopPropagation()} aria-label={`Verwijderen: ${s.title}`}><TrashIcon size={13} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelHeaderBadge}><MessageIcon size={12} /></span>
        <span className={styles.panelHeaderTitle}>Snippets</span>
      </div>

      <div className={styles.filterBar}>
        <span className={styles.filterIcon} aria-hidden="true">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M10 10l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        </span>
        <input
          className={styles.filterInput}
          type="text"
          placeholder="Zoeken…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoComplete="off"
          aria-label="Snippets filteren"
        />
        {filter && (
          <button type="button" className={styles.filterClear} onClick={() => setFilter('')} title="Wissen" aria-label="Filter wissen">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        )}
      </div>

      {renderGroup('global', 'Dermalink',            <GlobeIcon size={13} />,         styles.scopeIconGlobal, isOperator)}
      {hasOrg && renderGroup('org', orgName ?? 'Organisatie', <HospitalCrossIcon size={13} />, styles.scopeIconOrg, true)}
      {renderGroup('user',   'Eigen',                 <UserIcon size={13} />,          styles.scopeIconUser,   true)}

      {modal !== null && (
        <DoctorSnippetFormDialog
          open
          mode={modal.mode === 'edit' ? 'edit' : 'create'}
          scope={modal.mode === 'edit' ? modal.snippet.scope : modal.scope}
          initialTitle={modal.mode === 'edit' ? modal.snippet.title : ''}
          initialBody={modal.mode === 'edit' ? modal.snippet.body : ''}
          initialAutomatic={modal.mode === 'edit' ? modal.snippet.is_automatic : false}
          submitting={saving}
          serverError={formError}
          onClose={() => { if (saving) return; setModal(null); setFormError(null); }}
          onSubmit={(title, body, automatic) => {
            setFormError(null);
            if (modal.mode === 'create') postMutation.mutate({ title, body, scope: modal.scope, is_automatic: automatic });
            else patchMutation.mutate({ id: modal.snippet.id, title, body, is_automatic: automatic });
          }}
        />
      )}

      {deleteTarget !== null && (
        <ConfirmDialog
          title="Snippet verwijderen?"
          message={`"${deleteTarget.title}" wordt permanent verwijderd.`}
          confirmLabel="Verwijderen"
          variant="danger"
          confirming={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          onCancel={() => { if (!deleteMutation.isPending) setDeleteTarget(null); }}
        />
      )}
    </div>
  );
}
